import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import CTNPagination from '@ctn/ui/components/ctn-pagination';
import type { OfertaTrabajo, StrapiListRequest, StrapiListResponse } from '@/interfaces/strapi';
import { useStrapiApi, useStrapiParams } from '@/hooks';
import { getJobOffers } from '@/services/strapi';
import { JobOfferListItem } from '@/components';

type JobOfferListProps = {
	readonly className?: string;
	readonly initialPage?: number;
	readonly pageSize?: number;
};

const classNameComponent = 'c-cnm-job-offer-list';

export const JobOfferList = ({ className, initialPage = 1, pageSize = 5 }: JobOfferListProps) => {
	const wrapperClassName = classNames(classNameComponent, className);
	const [page, setPage] = useState(initialPage);

	const params = useStrapiParams({
		page,
		pageSize,
		sort: 'id:desc',
	});

	const { data, loading } = useStrapiApi<StrapiListResponse<OfertaTrabajo>, StrapiListRequest>(getJobOffers, {
		autoFetch: true,
		params,
	});

	const offers = data?.list ?? [];
	const pagination = data?.pagination;
	const paginationRef = useRef<any>(null);

	useEffect(() => {
		if (pagination && paginationRef.current?.goToPage) {
			paginationRef.current.goToPage(pagination.page ?? page);
		}
		if (pagination && typeof pagination.page === 'number' && pagination.page !== page) {
			setPage(pagination.page);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination?.page]);

	const handlePageChange = (newPage: number) => {
		if (newPage === page) return;
		setPage(newPage);
		window.scrollTo({ top: 600, behavior: 'smooth' });
	};

	return (
		<section className={wrapperClassName}>
			<h3 className={`${classNameComponent}__title`}>Ofertas activas</h3>
			{loading && <p>Cargando...</p>}

			{!loading && offers.length === 0 && <p>No hay ofertas disponibles.</p>}

			{!loading && offers.length > 0 && (
				<>
					<ul className={`${classNameComponent}__grid`}>
						{offers.map((p) => (
							<li key={p.id}>
								<JobOfferListItem jobOffer={p} />
							</li>
						))}
					</ul>

					{pagination && (
						<nav className="c-pagination u-mt-16" aria-label="Paginación">
							<CTNPagination
								ref={paginationRef}
								pageSize={pageSize}
								totalData={pagination.total ?? 0}
								locales={{
									text1: 'Página',
									text2: 'de',
									text3: 'Mostrando registros {0} de {1}',
								}}
								onPageChange={handlePageChange}
							/>
						</nav>
					)}
				</>
			)}
		</section>
	);
};
